import { createEntityAdapter, createSlice } from '@reduxjs/toolkit';
import { AsyncEntity } from 'src/global';
import qs from 'querystring';
import createAsyncThunkFromFetch from '../../store/middlewares/createAsyncThunkFromFetch';
import {
  addCompanyDetails,
  bindAccountToCompany,
  createCompany as createCompanyAPI,
  deleteCompany as deleteCompanyAPI,
  deleteCompanyDetails,
  getCompanies,
  getCompany,
  getCompanyAccounts,
  getHeadsetActivationCode,
  postCompanyActivate,
  unbindAccountToCompany,
  updateCompany as updateCompanyAPI,
  updateCompanyimages as updateCompanyimagesAPI,
  updateCompanyDetails,
} from './companies.api';
import { Company, CompanyQuery } from './companies.types';
import getUnixTime from 'date-fns/getUnixTime';
import { RootState } from 'src/store';

const name = 'companies';

const companyEntityAdapter = createEntityAdapter<AsyncEntity<Company>>({
  selectId: company => company.id,
  sortComparer: (prev, next) => {
    const prevUnix = getUnixTime(new Date(prev.activatedAt || ''));
    const nextUnix = getUnixTime(new Date(next.activatedAt || ''));

    return nextUnix > prevUnix ? 1 : -1;
  },
});

const companyQueryAdapter = createEntityAdapter<CompanyQuery>({
  selectId: q => q.query,
});

export const companyQuerySelectors = companyQueryAdapter.getSelectors<RootState>(state => state.companies.queries);

export const companiesSelectors = companyEntityAdapter.getSelectors();

export const generateHeadsetActivationCode = createAsyncThunkFromFetch(
  `${name}/generateHeadsetActivationCode`,
  getHeadsetActivationCode
);

export const fetchCompanies = createAsyncThunkFromFetch(`${name}/fetchCompanies`, getCompanies);
export const fetchCompany = createAsyncThunkFromFetch(`${name}/fetchCompany`, getCompany);
export const deleteCompany = createAsyncThunkFromFetch(`${name}/deleteCompany`, deleteCompanyAPI);
export const createCompany = createAsyncThunkFromFetch(`${name}/createCompany`, createCompanyAPI);
export const updateCompany = createAsyncThunkFromFetch(`${name}/updateCompany`, updateCompanyAPI);
export const updateCompanyimages = createAsyncThunkFromFetch(`${name}/updateCompany`, updateCompanyimagesAPI);
export const fetchCompanyAccounts = createAsyncThunkFromFetch(`${name}/fetchCompanyAccounts`, getCompanyAccounts);
export const addAccountToCompany = createAsyncThunkFromFetch(`${name}/addAccountToCompany`, bindAccountToCompany);
export const removeAccountFromCompany = createAsyncThunkFromFetch(
  `${name}/removeAccountFromCompany`,
  unbindAccountToCompany
);
export const activateCompany = createAsyncThunkFromFetch(`${name}/activateCompany`, postCompanyActivate);

export const addDetailsToCompany = createAsyncThunkFromFetch(`${name}/addDetailsToCompany`, addCompanyDetails);
export const saveCompanyDetails = createAsyncThunkFromFetch(`${name}/saveCompanyDetails`, updateCompanyDetails);
export const deleteDetailsFromCompany = createAsyncThunkFromFetch(
  `${name}/deleteDetailsFromCompany`,
  deleteCompanyDetails
);

const companiesSlice = createSlice({
  name,
  initialState: companyEntityAdapter.getInitialState({
    queries: companyQueryAdapter.getInitialState(),
  }),
  reducers: {},
  extraReducers: builder => {
    // Companies
    builder.addCase(fetchCompanies.pending, (state, { meta }) => {
      const { name = 'all' } = meta.arg;
      const query = qs.stringify({ name });
      const prevQuery = companyQueryAdapter.getSelectors().selectById(state.queries, query);
      const { items: prevItems = [] } = prevQuery || {};

      companyQueryAdapter.upsertOne(state.queries, {
        query,
        hasError: false,
        items: [...prevItems],
        isPending: true,
      });
    });
    builder.addCase(fetchCompanies.fulfilled, (state, { payload, meta }) => {
      const { name = 'all' } = meta.arg;
      const id = qs.stringify({ name });

      companyQueryAdapter.updateOne(state.queries, {
        id,
        changes: {
          hasError: false,
          items: payload.items.map(p => p.id),
          isPending: false,
        },
      });

      companyEntityAdapter.upsertMany(state, payload.items);
    });
    builder.addCase(fetchCompanies.rejected, (state, { meta }) => {
      const { name = 'all' } = meta.arg;
      const id = qs.stringify({ name });

      companyQueryAdapter.updateOne(state.queries, {
        id,
        changes: {
          hasError: true,
          isPending: false,
        },
      });
    });
    builder.addCase(deleteCompany.pending, (state, { meta }) => {
      companyEntityAdapter.updateOne(state, {
        id: meta.arg.companyId,
        changes: { isPending: true },
      });
    });
    builder.addCase(deleteCompany.fulfilled, (state, { meta }) => {
      companyEntityAdapter.removeOne(state, meta.arg.companyId);
    });
    builder.addCase(deleteCompany.rejected, (state, { meta }) => {
      companyEntityAdapter.updateOne(state, {
        id: meta.arg.companyId,
        changes: { isPending: false, hasError: true },
      });
    });
    builder.addCase(createCompany.fulfilled, (state, { payload }) => {
      companyEntityAdapter.addOne(state, payload);
    });
    builder.addCase(fetchCompany.fulfilled, (state, { payload }) => {
      companyEntityAdapter.upsertOne(state, payload);
    });
    builder.addCase(updateCompany.pending, (state, { meta }) => {
      companyEntityAdapter.updateOne(state, {
        id: meta.arg.companyId,
        changes: { isPending: true },
      });
    });
    builder.addCase(updateCompany.fulfilled, (state, { meta, payload }) => {
      companyEntityAdapter.updateOne(state, {
        id: meta.arg.companyId,
        changes: { ...payload, isPending: false },
      });
    });
    builder.addCase(fetchCompanyAccounts.fulfilled, (state, { meta, payload }) => {
      companyEntityAdapter.updateOne(state, {
        id: meta.arg.companyId,
        changes: { ...payload, isPending: false },
      });
    });
    builder.addCase(addAccountToCompany.fulfilled, (state, { meta, payload }) => {
      const { companyId } = meta.arg;
      const company = companiesSelectors.selectById(state, companyId) as Company;
      companyEntityAdapter.updateOne(state, {
        id: companyId,
        changes: {
          accounts: [...company.accounts, ...payload],
        },
      });
    });
    builder.addCase(removeAccountFromCompany.fulfilled, (state, { meta }) => {
      const { companyId, accountId } = meta.arg;
      const company = companiesSelectors.selectById(state, companyId) as Company;
      companyEntityAdapter.updateOne(state, {
        id: companyId,
        changes: {
          accounts: company.accounts.filter(a => a.id !== accountId),
        },
      });
    });
    builder.addCase(activateCompany.fulfilled, (state, { meta, payload }) => {
      const { companyId } = meta.arg;
      companyEntityAdapter.updateOne(state, {
        id: companyId,
        changes: payload,
      });
    });
    builder.addCase(addDetailsToCompany.fulfilled, (state, { meta, payload }) => {
      const nextState = {
        ...companyEntityAdapter.getSelectors().selectById(state, meta.arg.companyId),
      };

      if (nextState) {
        nextState.details = nextState.details?.map(d => (d.id === payload.id ? payload : d));
        companyEntityAdapter.updateOne(state, {
          id: meta.arg.companyId,
          changes: nextState,
        });
      }
    });
    builder.addCase(saveCompanyDetails.fulfilled, (state, { meta, payload }) => {
      const nextState = {
        ...companyEntityAdapter.getSelectors().selectById(state, meta.arg.companyId),
      };

      if (nextState) {
        nextState.details = nextState.details?.map(d => (d.id === payload.id ? payload : d));
        companyEntityAdapter.updateOne(state, {
          id: meta.arg.companyId,
          changes: nextState,
        });
      }
    });
    builder.addCase(deleteDetailsFromCompany.fulfilled, (state, { meta }) => {
      const nextState = {
        ...companyEntityAdapter.getSelectors().selectById(state, meta.arg.companyId),
      };

      if (nextState) {
        nextState.details = nextState.details?.filter(d => d.id !== meta.arg.recordId);
        companyEntityAdapter.updateOne(state, {
          id: meta.arg.companyId,
          changes: nextState,
        });
      }
    });
  },
});

const { reducer } = companiesSlice;

export default reducer;
